import authService from './authService';
import {
  InvoiceInvalidation,
  InvoiceInvalidationCreatePayload,
  InvoiceInvalidationListFilters,
  InvoiceInvalidationListResult,
} from '../types/invoiceInvalidation';
import { ApiResponse } from '../types/common';

/**
 * Service para Invalidação (Inutilização) de NF-e/NFC-e
 *
 * Endpoints (base /api/v1):
 *   GET    /invoice-invalidations
 *   GET    /invoice-invalidations/:id
 *   POST   /invoice-invalidations
 *
 * Todos os endpoints exigem papel `admin` (permissão
 * invoice_invalidation.read / invoice_invalidation.create).
 */

interface PaginationPayload {
  page?: number;
  page_size?: number;
  pageSize?: number;
  total?: number;
  total_items?: number;
  total_pages?: number;
  totalPages?: number;
}

const normalizePagination = (
  pg: PaginationPayload | undefined,
  filters: InvoiceInvalidationListFilters,
  itemsLength: number,
) => ({
  page: pg?.page ?? filters.page ?? 1,
  pageSize: pg?.page_size ?? pg?.pageSize ?? filters.pageSize ?? 20,
  total: pg?.total_items ?? pg?.total ?? itemsLength,
  totalPages: pg?.total_pages ?? pg?.totalPages ?? 1,
});

class InvoiceInvalidationService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private buildListParams(filters: InvoiceInvalidationListFilters): string {
    const params = new URLSearchParams();
    params.set('page', String(filters.page ?? 1));
    params.set('page_size', String(filters.pageSize ?? 20));
    if (filters.company_id !== undefined) params.set('company_id', String(filters.company_id));
    if (filters.status_id !== undefined) params.set('status_id', String(filters.status_id));
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    return params.toString();
  }

  async list(filters: InvoiceInvalidationListFilters): Promise<InvoiceInvalidationListResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoice-invalidations?${this.buildListParams(filters)}`);
    if (!response.ok) {
      if (response.status === 403) throw new Error('Acesso restrito a administradores');
      throw new Error('Falha ao carregar invalidações');
    }
    const json = await response.json();
    const items: InvoiceInvalidation[] = Array.isArray(json.data) ? json.data : [];
    return {
      data: items,
      pagination: normalizePagination(json.pagination, filters, items.length),
    };
  }

  async getById(id: number): Promise<InvoiceInvalidation> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoice-invalidations/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Invalidação não encontrada');
      if (response.status === 403) throw new Error('Acesso restrito a administradores');
      throw new Error('Falha ao carregar invalidação');
    }
    const data: ApiResponse<InvoiceInvalidation> = await response.json();
    return data.data!;
  }

  async create(payload: InvoiceInvalidationCreatePayload): Promise<InvoiceInvalidation> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/invoice-invalidations', payload);
    const json = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error(json?.message || 'Acesso restrito a administradores');
      }
      throw new Error(json?.message || 'Falha ao submeter invalidação');
    }
    return (json as ApiResponse<InvoiceInvalidation>).data!;
  }

  async retry(id: number): Promise<InvoiceInvalidation> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/invoice-invalidations/${id}/retry`, {});
    const json = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error(json?.message || 'Acesso restrito a administradores');
      }
      throw new Error(json?.message || 'Falha ao reenviar invalidação');
    }
    return (json as ApiResponse<InvoiceInvalidation>).data!;
  }
}

export const invoiceInvalidationService = new InvoiceInvalidationService();
export default invoiceInvalidationService;
